import * as THREE from 'three';
// Import PistolSkill for stats
import { pistolSkill } from '../SkillAdapter.js';

// Pistol.js
// Weapon with XP leveling and scaling fire rate/damage

export default class Pistol {
  constructor(scene, player) {
    this.scene = scene;
    this.player = player;
    this.cooldown = pistolSkill.getReloadTime();
    this.shotsBeforeCooldown = pistolSkill.getMagazineSize();
    this.shotsFired = 0; // Track shots fired
    this.lastShot = 0;
    this.isFiring = false; // Track if the pistol is currently firing
    this.isReloading = false; // Track if pistol is reloading
    this.mouseHoldStartTime = 0; // Track when mouse was pressed
    this.burstInProgress = false; // Track if burst fire is in progress
    this.burstShotsRemaining = 0; // Track remaining shots in burst
    this.burstDelay = 150; // Increased delay between burst shots for better reliability
    this.isMouseButtonDown = false; // Track if the primary fire button is currently held down
    this.justPressed = false; // Track if this is the first fire call after a mousedown
    this.baseDamage = 10;
    this.xp = 0;
    this.name = "Pistol";
    this.icon = "/assets/pistol.png"; // placeholder image
    this.damage = 25;
    this.range = 100;
    this.equipped = false;
    this.isAiming = false; // Track aiming state
    
    // Dynamically get level from pistolSkill when available
    Object.defineProperty(this, 'level', {
      get: function() {
        // Always get level from pistolSkill when available
        if (window.pistolSkill) {
          return window.pistolSkill.level;
        }
        return 1; // Default level if pistolSkill not available
      },
      configurable: true
    });
    
    // Auto-register for level up events to update properties
    this.setupLevelChangeListener();
    
    // Accuracy values
    this.baseSpread = 0.00046875; // Halved again for further increased accuracy
    this.aimingSpread = 0.0003125; // Halved again for further increased aiming accuracy
    
    // Store weapon attachment point position
    this.attachmentPoint = null;
    
    // Reference to the camera for positioning
    this.camera = null;
    
    // Set debug mode for troubleshooting
    this.debug = false; // Disable debug to hide projectile path
    
    // Create simple weapon model
    this.createModel();
    
    // Update interval for smoother following
    this.lastUpdateTime = 0;
    this.updateInterval = 16; // ~60fps
    
    // Animation smoothing
    this.lastQuaternion = new THREE.Quaternion();
    this.targetQuaternion = new THREE.Quaternion();
    this.rotationSpeed = 0.1; // Adjust for smoother rotation
    
    // Animation frame ID for proper cancellation
    this.animationFrameId = null;
    
    // Default crosshair offset - will be calibrated
    this.crosshairOffset = new THREE.Vector2(-0.2, 0);
    this.calibrationPoints = [];
    
    // Load any previously saved crosshair offset
    this.loadSavedOffset();
    
    // Register debug console command
    window.toggleWeaponDebug = this.toggleDebug.bind(this);
    console.log("Weapon debugging available! Use window.toggleWeaponDebug() in console to enable/disable");
  }

  createModel() {
    // Simple pistol model
    this.model = new THREE.Group();
    
    // Gun body
    const gunBody = new THREE.Mesh(
      new THREE.BoxGeometry(0.2, 0.15, 0.5),
      new THREE.MeshStandardMaterial({ color: 0x222222 })
    );
    
    // Gun handle - position it so that the handle is at (0,0,0) for attachment
    const gunHandle = new THREE.Mesh(
      new THREE.BoxGeometry(0.1, 0.25, 0.15),
      new THREE.MeshStandardMaterial({ color: 0x333333 })
    );
    gunHandle.position.y = -0.125; // Center the handle at origin for attachment
    gunHandle.position.z = 0.0;  // Move back for better grip alignment
    
    // Gun barrel
    const gunBarrel = new THREE.Mesh(
      new THREE.CylinderGeometry(0.05, 0.05, 0.4, 8),
      new THREE.MeshStandardMaterial({ color: 0x444444 })
    );
    gunBarrel.rotation.x = Math.PI / 2;
    gunBarrel.position.z = 0.3;  // Extended barrel
    gunBarrel.position.y = 0.05; // Raised slightly
    
    // Create a barrel end point for projectile origin
    this.barrelTip = new THREE.Object3D();
    this.barrelTip.position.set(0, 0, 0.4); // Position at the end of the barrel
    gunBarrel.add(this.barrelTip);
    
    // Create a pistol group with proper origin at handle
    const pistolParts = new THREE.Group();
    pistolParts.add(gunBody);
    pistolParts.add(gunBarrel);
    
    // Position the body relative to the handle
    gunBody.position.y = 0.15;  // Above the handle
    gunBody.position.z = 0.2;   // Forward of the handle
    
    // Add handle first (at origin), then add parts relative to it
    this.model.add(gunHandle);
    this.model.add(pistolParts);
    
    // Set castShadow on all meshes
    this.model.traverse(child => {
      if (child instanceof THREE.Mesh) {
        child.castShadow = true;
        child.receiveShadow = true;
      }
    });
    
    // Initial orientation for being held in the player's hand
    this.model.rotation.set(0, 0, 0);
    
    // Don't add to scene here - will be handled by equipment system
  }
  
  equip() {
    // Add model to scene when equipped
    if (this.scene && this.model) {
      // Store camera reference for updates
      this.camera = window.camera;
      
      // Add to scene only if not already added
      if (!this.model.parent) {
        this.scene.add(this.model);
      }
      this.equipped = true;
      
      // Get the player model attachment point
      this.updateAttachmentPoint();
      
      // Force an immediate update of position
      this.updatePosition();
      
      // Ensure continuous updates
      this.continuousUpdate();
    }
  }
  
  // Continuous update function for smoother following
  continuousUpdate() {
    // Stop updating if no longer equipped
    if (!this.equipped) return;
    
    // Update position and orientation
    this.updateAttachmentPoint();
    this.updatePosition();
    
    // Schedule next update and store the animation frame ID
    this.animationFrameId = requestAnimationFrame(() => this.continuousUpdate());
  }
  
  // Update the attachment point from player model
  updateAttachmentPoint() {
    // If player has a playerModel, position gun at attachment point
    const playerController = this.player;
    if (playerController && playerController.getPlayerModel) {
      const playerModel = playerController.getPlayerModel();
      if (playerModel) {
        const weaponAttachment = playerModel.getWeaponAttachmentPoint();
        if (weaponAttachment) {
          // Get world position
          const weaponWorldPosition = new THREE.Vector3();
          weaponAttachment.getWorldPosition(weaponWorldPosition);
          
          // Get world quaternion for proper orientation
          const weaponWorldQuaternion = new THREE.Quaternion();
          weaponAttachment.getWorldQuaternion(weaponWorldQuaternion);
          
          // Cache both position and orientation
          this.attachmentPoint = weaponWorldPosition;
          this.attachmentQuaternion = weaponWorldQuaternion;
          
          // Also get player's world orientation to align the gun with player direction
          const playerWorldQuaternion = new THREE.Quaternion();
          playerModel.getWorldQuaternion(playerWorldQuaternion);
          this.playerQuaternion = playerWorldQuaternion;
          
          return true;
        }
      }
    }
    return false;
  }
  
  // Update pistol position directly from attachment point
  updatePosition() {
    if (!this.model || !this.equipped) return;
    
    if (!this.attachmentPoint) {
      // If no attachment point, attempt to get it
      if (!this.updateAttachmentPoint()) {
        // Fallback to camera-relative positioning if no attachment point
        this.updateCameraPosition();
        return;
      }
    }
    
    // Update position from attachment point
    this.model.position.copy(this.attachmentPoint);
    
    // Apply world quaternion from attachment point if available
    if (this.attachmentQuaternion) {
      // Get player model to check if currently aiming
      const playerModel = this.player?.getPlayerModel();
      const isAiming = playerModel?.isCurrentlyAiming || false;
      const isShooting = playerModel?.isCurrentlyShooting || false;
      
      if (isAiming) {
        // When aiming, point gun toward where they're looking
        if (this.camera) {
          const cameraDirection = new THREE.Vector3(0, 0, -1);
          cameraDirection.applyQuaternion(this.camera.quaternion);
          // Allow full vertical aiming - removing the constraint
          // that was keeping the gun level
          cameraDirection.normalize();
          
          // Create a temporary object to point at the target
          const lookAtMatrix = new THREE.Matrix4();
          lookAtMatrix.lookAt(new THREE.Vector3(), cameraDirection, new THREE.Vector3(0, 1, 0));
          this.targetQuaternion.setFromRotationMatrix(lookAtMatrix);
        }
      } else if (isShooting) {
        // When shooting with one hand, point gun forward
        this.targetQuaternion.copy(this.attachmentQuaternion);
      } else {
        // When idle (not aiming or shooting), point gun downward toward ground
        this.targetQuaternion.copy(this.attachmentQuaternion);
        const downwardRotation = new THREE.Quaternion().setFromEuler(
          new THREE.Euler(Math.PI / 2, 0, 0) // 90 degrees downward
        );
        this.targetQuaternion.multiply(downwardRotation);
      }
      
      // Smooth rotation interpolation
      this.model.quaternion.slerp(this.targetQuaternion, this.rotationSpeed);
      this.lastQuaternion.copy(this.model.quaternion);
    }
  }
  
  // Fallback to camera-relative positioning
  updateCameraPosition() {
    if (!this.camera) this.camera = window.camera;
    if (!this.camera) return;
    
    // Get camera position and direction
    const cameraWorldPos = new THREE.Vector3();
    this.camera.getWorldPosition(cameraWorldPos);
    
    const cameraDirection = new THREE.Vector3();
    this.camera.getWorldDirection(cameraDirection);
    
    // Position pistol in view
    this.model.position.copy(cameraWorldPos);
    this.model.position.x += cameraDirection.x * 0.5 + 0.3;
    this.model.position.y += cameraDirection.y * 0.5 - 0.2;
    this.model.position.z += cameraDirection.z * 0.5 - 0.5;
    
    // Make gun face same direction as camera
    this.model.lookAt(cameraWorldPos.clone().add(cameraDirection));
    
    if (this.debug) console.log("Updated pistol with camera position:", this.model.position);
  }
  
  unequip() {
    // Remove model from scene when unequipped
    if (this.scene && this.model) {
      // Only try to remove if it's actually in the scene
      if (this.model.parent) {
        this.scene.remove(this.model);
      }
      
      // Clear attachment points
      this.attachmentPoint = null;
      this.attachmentQuaternion = null;
      
      // Cancel any pending animation frame
      if (this.animationFrameId !== null) {
        cancelAnimationFrame(this.animationFrameId);
        this.animationFrameId = null;
      }
      
      // Stop continuous updates
      this.equipped = false;
      
      // Lower player's arm when unequipping
      if (this.player && this.player.getPlayerModel) {
        const playerModel = this.player.getPlayerModel();
        if (playerModel) {
          playerModel.setAiming(false);
        }
      }
    }
  }
  
  update(camera) {
    if (!this.model || !this.equipped) return;
    
    // Save camera reference for later use
    if (camera) this.camera = camera;
    
    // Update is now handled by continuousUpdate to ensure smooth following
  }
  
  // Set the weapon attachment point from the player model
  setWeaponAttachmentPoint(position, quaternion) {
    if (!position) return;
    this.attachmentPoint = position.clone();
    if (quaternion) this.attachmentQuaternion = quaternion.clone();
    
    // Make sure the model position is updated right away
    this.updatePosition();
  }

  /**
   * Set the aiming state of the pistol
   * @param {boolean} isAiming - Whether the player is aiming
   */
  setAiming(isAiming) {
    this.isAiming = isAiming;
    
    if (this.debug) {
      console.log(`Pistol aiming set to: ${isAiming}`);
    }
  }

  /**
   * Get the current spread value based on aiming state
   * @returns {number} The current bullet spread value
   */
  getCurrentSpread() {
    return this.isAiming ? this.aimingSpread : this.baseSpread;
  }

  /**
   * Start reload animation and set cooldown
   */
  reload() {
    if (this.isReloading) return;
    
    this.isReloading = true;
    console.log("Reloading pistol...");
    
    // Get current reload time from pistolSkill
    this.cooldown = pistolSkill.getReloadTime();
    
    // Trigger reload animation on player model if available
    if (this.player?.getPlayerModel) {
      const playerModel = this.player.getPlayerModel();
      if (playerModel && typeof playerModel.triggerReloadAnimation === 'function') {
        playerModel.triggerReloadAnimation();
      }
    }
    
    // Trigger the reload UI if available - pass this pistol directly
    if (typeof window.showReloadUI === 'function') {
      window.showReloadUI(this.cooldown, this);
    }
    
    // Reset after cooldown period
    setTimeout(() => {
      this.shotsFired = 0;
      this.isReloading = false;
      console.log("Pistol reloaded and ready to fire");
    }, this.cooldown);
  }

  // Call this method when the fire button is pressed down
  startFiring() {
    this.isMouseButtonDown = true;
    this.justPressed = true; // Flag the initial press
    // Record start time only if it hasn't been recorded yet (prevents reset during hold)
    if (this.mouseHoldStartTime === 0) {
        this.mouseHoldStartTime = performance.now();
        console.log("Initial mouse press registered.");
    }
  }

  // Call this method when the fire button is released
  stopFiring() {
    this.isMouseButtonDown = false;
    this.mouseHoldStartTime = 0; // Reset hold timer
    this.burstInProgress = false; // Cancel any burst in progress
    this.justPressed = false; // Reset on release
    console.log("Mouse button released, resetting hold time.");
  }

  fire(player) {
    // Add detailed logging at the start
    console.log(`[Fire Start] Level: ${this.level}, isMouseButtonDown: ${this.isMouseButtonDown}, burstInProgress: ${this.burstInProgress}, isFiring: ${this.isFiring}, justPressed: ${this.justPressed}`);

    // Check for a valid player object
    if (!player) {
        console.error("[Fire] Player is undefined");
        return false;
    }

    // If already reloading, can't fire
    if (this.isReloading) {
        console.log("[Fire] Cannot fire - reloading");
        return false;
    }

    // Check for burst fire conditions first (only if not already bursting)
    if (this.level >= 3 && !this.burstInProgress && this.isMouseButtonDown) {
        if (this.mouseHoldStartTime > 0) {
            const holdTime = performance.now() - this.mouseHoldStartTime;
            if (holdTime >= 1250) { // 1.25 seconds
                console.log(`[Fire] Checking burst: Hold time: ${holdTime.toFixed(2)}ms`);
                this.shotsBeforeCooldown = pistolSkill.getMagazineSize();
                if (this.shotsFired + 3 <= this.shotsBeforeCooldown) {
                    console.log("[Fire] Starting burst fire!");
                    this.burstInProgress = true;
                    this.burstShotsRemaining = 3;
                    this.executeBurstFire(player);
                    this.justPressed = false; // Burst consumes the initial press state
                    return true; // Burst initiated, exit
                } else {
                    console.log("[Fire] Not enough ammo for burst fire");
                    return false;
                }
            }
        } else {
            console.warn("[Fire] mouseHoldStartTime is 0 during burst check");
        }
    }

    // If a burst is in progress, don't allow regular shots
    if (this.burstInProgress) {
        console.log("[Fire] Blocked by burstInProgress");
        return false;
    }

    // Check ammo before any regular shot attempt
    this.shotsBeforeCooldown = pistolSkill.getMagazineSize();
    if (this.shotsFired >= this.shotsBeforeCooldown) {
        this.reload();
        return false;
    }

    // --- Regular Firing Logic --- 
    let allowRegularShot = false;
    console.log(`[Fire] Entering regular fire check. Level: ${this.level}`);

    if (this.level < 5) { // Semi-auto levels (1-4)
        console.log(`[Fire] Semi-auto path (Level ${this.level}). justPressed: ${this.justPressed}, isFiring: ${this.isFiring}`);
        // Allow shot ONLY on the initial press
        if (this.justPressed) {
            console.log("[Fire] Semi-auto: Firing on initial press.");
            allowRegularShot = true;
            this.justPressed = false; // Consume the 'justPressed' state after the first shot attempt
        } else {
            // Block if button is held (justPressed is false)
            console.log("[Fire] Semi-auto: Blocked by hold (justPressed is false).");
        }
    } else { // Full-auto levels (5+)
        console.log(`[Fire] Full-auto path (Level ${this.level}). isFiring: ${this.isFiring}`);
        // Allow shot if cooldown is over
        if (!this.isFiring) {
            console.log("[Fire] Full-auto: Firing (cooldown ended).");
            allowRegularShot = true;
        } else {
            console.log("[Fire] Full-auto: Blocked by cooldown.");
        }
    }

    console.log(`[Fire] Final check. allowRegularShot: ${allowRegularShot}`);

    if (allowRegularShot) {
        // --- If we got here, proceed with a regular shot --- 
        let playerMesh;
        // Handle different player object types
        if (typeof player.getMesh === 'function') {
            playerMesh = player.getMesh();
        } 
        else if (player.mesh) {
            playerMesh = player.mesh;
        }
        else {
            console.error("[Fire] Cannot obtain player mesh...");
            return false;
        }
        if (!playerMesh) {
            console.error("[Fire] Player mesh is null...");
            return false;
        }
        if (!this.equipped) {
            console.log("[Fire] Blocked fire: pistol not equipped");
            return false;
        }

        console.log("[Fire] EXECUTE SHOT");
        this.executeShot(player);
        return true;
    } else {
        // Shot not allowed (either semi-auto hold or cooldown active)
        console.log("[Fire] Shot NOT allowed this frame.");
        return false;
    }
  }
  
  /**
   * Execute a single shot
   * @param {Object} player - The player object
   */
  executeShot(player) {
    // Force update attachment point before firing
    this.updateAttachmentPoint();
    this.updatePosition();
    
    // Raise player's arm for shooting animation
    if (player.getPlayerModel) {
        const playerModel = player.getPlayerModel();
        if (playerModel && playerModel.triggerShootingAnimation) {
            playerModel.triggerShootingAnimation();
        }
    }

    // Mark as firing and increment shots fired
    this.isFiring = true;
    this.shotsFired++;
    
    // Reset firing flag after small delay - shorter delay for level 5+ (full auto)
    setTimeout(() => {
        this.isFiring = false;
    }, this.level >= 5 ? 250 : 100); // Increased from 150ms to 250ms for full auto

    this.lastShot = performance.now();
    this.createMuzzleFlash();
    this.playSound();
    console.log(`Pistol fired! Shot ${this.shotsFired}/${this.shotsBeforeCooldown}`);

    // FIXED - Get projectile origin from barrel tip
    const origin = new THREE.Vector3();
    if (this.barrelTip) {
        this.barrelTip.getWorldPosition(origin);
        console.log("Using barrel tip for projectile origin:", origin);
    } else {
        // Fallback if barrel tip isn't available
        origin.copy(playerMesh.position);
        origin.y += 1.5; // Offset for player height
        console.log("Fallback to player position for origin:", origin);
    }

    // Get direction as a ray through the center of the screen
    const camera = window.camera;
    
    // --- NEW METHOD: Use camera's forward direction directly --- 
    const direction = new THREE.Vector3();
    camera.getWorldDirection(direction); // Get the camera's forward vector
    direction.normalize();
    // --- END NEW METHOD ---

    // Log for debugging
    console.log("Final direction (from camera):", direction);
    
    direction.normalize();

    // Create projectile using projectile pool
    if (window.projectilePool) {
        const projectile = window.projectilePool.getAvailableProjectile();
        if (projectile) {
            // Get current projectile speed from skill system
            const currentSpeed = pistolSkill.getProjectileSpeed();
            console.log("Projectile speed:", currentSpeed);

            projectile.activate(origin, direction, currentSpeed, this); // Pass speed here
            console.log("Projectile created at", origin, "with direction", direction);
        }
    } else {
        console.error("ProjectilePool not available - projectiles won't work!");
    }

    // Check if we need to reload after this shot
    if (this.shotsFired >= this.shotsBeforeCooldown) {
        this.reload();
    }
  }
  
  /**
   * Execute burst fire sequence
   * @param {Object} player - The player object
   */
  executeBurstFire(player) {
    if (this.burstShotsRemaining <= 0 || !this.burstInProgress) {
        console.log("Burst fire complete or cancelled");
        this.burstInProgress = false;
        return;
    }

    console.log(`Burst shot remaining: ${this.burstShotsRemaining}`);
    // Execute one shot of the burst
    this.executeShot(player);
    this.burstShotsRemaining--;

    // Schedule next burst shot if there are more remaining
    if (this.burstShotsRemaining > 0) {
        setTimeout(() => {
            this.executeBurstFire(player);
        }, this.burstDelay);
    } else {
        console.log("Burst fire sequence complete");
        this.burstInProgress = false;
    }
  }
  
  createMuzzleFlash() {
    // Only create muzzle flash if barrel tip exists
    if (!this.barrelTip || !this.scene) return;
    
    // Get world position of barrel tip
    const muzzlePosition = new THREE.Vector3();
    this.barrelTip.getWorldPosition(muzzlePosition);
    
    // Create a simple muzzle flash
    const geometry = new THREE.SphereGeometry(0.1, 8, 8);
    const material = new THREE.MeshBasicMaterial({
      color: 0xffff00,
      opacity: 0.8,
      transparent: true
    });
    const flash = new THREE.Mesh(geometry, material);
    flash.position.copy(muzzlePosition);
    this.scene.add(flash);
    
    // Remove after short delay
    setTimeout(() => {
      this.scene.remove(flash);
      geometry.dispose();
      material.dispose();
    }, 50);
  }

  playSound() {
    // Simple placeholder for sound effects
    console.log("Bang!");
  }

  /**
   * Get the current damage from PistolSkill
   * @returns {number} Current damage value
   */
  getDamage() {
    return pistolSkill.getDamage();
  }

  // Create a thumbnail representation for inventory
  createThumbnail(container) {
    // Create a simple pistol icon as SVG
    const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
    svg.setAttribute("viewBox", "0 0 100 100");
    svg.setAttribute("width", "40");
    svg.setAttribute("height", "40");
    
    // Draw pistol shape
    const gunBody = document.createElementNS("http://www.w3.org/2000/svg", "rect");
    gunBody.setAttribute("x", "30");
    gunBody.setAttribute("y", "40");
    gunBody.setAttribute("width", "40");
    gunBody.setAttribute("height", "15");
    gunBody.setAttribute("fill", "#333");
    svg.appendChild(gunBody);
    
    const gunHandle = document.createElementNS("http://www.w3.org/2000/svg", "rect");
    gunHandle.setAttribute("x", "50");
    gunHandle.setAttribute("y", "55");
    gunHandle.setAttribute("width", "10");
    gunHandle.setAttribute("height", "25");
    gunHandle.setAttribute("fill", "#555");
    svg.appendChild(gunHandle);
    
    const gunBarrel = document.createElementNS("http://www.w3.org/2000/svg", "rect");
    gunBarrel.setAttribute("x", "10");
    gunBarrel.setAttribute("y", "42");
    gunBarrel.setAttribute("width", "20");
    gunBarrel.setAttribute("height", "10");
    gunBarrel.setAttribute("fill", "#222");
    svg.appendChild(gunBarrel);
    
    container.appendChild(svg);
    return svg;
  }

  // Listen for pistol level changes
  setupLevelChangeListener() {
    window.addEventListener('pistol-level-up', (event) => {
      console.log(`Pistol: Detected level up to ${event.detail.level}, updating properties`);
      
      // Update pistol properties based on new level
      if (window.pistolSkill) {
        this.cooldown = window.pistolSkill.getReloadTime();
        this.shotsBeforeCooldown = window.pistolSkill.getMagazineSize();
      }
    });
  }

  // Calibration system to find the perfect crosshair offset
  startCalibration() {
    // Clear any existing calibration points
    this.clearCalibrationPoints();
    
    // Create horizontal line of test points
    const numPoints = 11; // -0.5 to 0.5 in 0.1 increments
    const step = 0.1;
    
    for (let i = 0; i < numPoints; i++) {
        const x = -0.5 + (i * step);
        const testOffset = new THREE.Vector2(x, 0);
        
        // Create a visible sphere at this test point
        const point = new THREE.Mesh(
            new THREE.SphereGeometry(0.3, 8, 8),
            new THREE.MeshBasicMaterial({
                color: 0xff0000,
                transparent: true,
                opacity: 0.8
            })
        );
        
        // Position the point using raycaster with this offset
        const raycaster = new THREE.Raycaster();
        raycaster.setFromCamera(testOffset, window.camera);
        
        // Project from camera position
        const direction = raycaster.ray.direction;
        const distance = 30; // Visible distance
        const position = window.camera.position.clone().addScaledVector(direction, distance);
        
        point.position.copy(position);
        point.userData = { offset: testOffset };
        
        if (window.scene) {
            window.scene.add(point);
            this.calibrationPoints.push(point);
        }
        
        // Add number label
        const textSprite = this.createTextSprite(x.toFixed(1));
        textSprite.position.copy(position);
        textSprite.position.y += 0.5; // Position above the point
        
        if (window.scene) {
            window.scene.add(textSprite);
            this.calibrationPoints.push(textSprite);
        }
    }
    
    // Setup click event to select a point
    this.setupCalibrationClickHandler();
    
    console.log("Calibration started: Aim at your crosshair and click the red dot that aligns with it.");
    console.log("Press ESC to cancel calibration.");
  }
  
  // Create a text sprite to show offset values
  createTextSprite(text) {
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    canvas.width = 64;
    canvas.height = 32;
    
    context.fillStyle = 'white';
    context.font = '24px Arial';
    context.fillText(text, 4, 24);
    
    const texture = new THREE.CanvasTexture(canvas);
    const material = new THREE.SpriteMaterial({ map: texture });
    return new THREE.Sprite(material);
  }
  
  // Setup click handler for calibration
  setupCalibrationClickHandler() {
    // Store the original event listeners
    this.originalClickHandler = document.onclick;
    this.originalKeyHandler = document.onkeydown;
    
    // Setup new click handler
    document.onclick = (event) => {
        // Cast ray from camera to see what was clicked
        const raycaster = new THREE.Raycaster();
        const mouse = new THREE.Vector2(
            (event.clientX / window.innerWidth) * 2 - 1,
            -(event.clientY / window.innerHeight) * 2 + 1
        );
        
        raycaster.setFromCamera(mouse, window.camera);
        const intersects = raycaster.intersectObjects(this.calibrationPoints);
        
        if (intersects.length > 0) {
            const selected = intersects[0].object;
            if (selected.userData && selected.userData.offset) {
                this.setCrosshairOffset(selected.userData.offset);
                this.clearCalibrationPoints();
                console.log(`Crosshair calibrated to offset: ${selected.userData.offset.x.toFixed(2)}, ${selected.userData.offset.y.toFixed(2)}`);
            }
        }
    };
    
    // Setup escape key to cancel
    document.onkeydown = (event) => {
        if (event.key === 'Escape') {
            this.clearCalibrationPoints();
            console.log("Calibration cancelled.");
        }
    };
  }
  
  // Set and save the crosshair offset
  setCrosshairOffset(offset) {
    this.crosshairOffset = offset;
    // Optionally save to localStorage for persistence
    localStorage.setItem('crosshairOffset', JSON.stringify({
        x: offset.x,
        y: offset.y
    }));
  }
  
  // Clear calibration points and restore handlers
  clearCalibrationPoints() {
    // Remove all calibration objects
    for (const point of this.calibrationPoints) {
        window.scene.remove(point);
    }
    this.calibrationPoints = [];
    
    // Restore original event handlers
    document.onclick = this.originalClickHandler;
    document.onkeydown = this.originalKeyHandler;
  }
  
  // Check for saved offset on load
  loadSavedOffset() {
    const saved = localStorage.getItem('crosshairOffset');
    if (saved) {
        try {
            const offset = JSON.parse(saved);
            this.crosshairOffset = new THREE.Vector2(offset.x, offset.y);
            console.log(`Loaded saved crosshair offset: ${offset.x.toFixed(2)}, ${offset.y.toFixed(2)}`);
        } catch (e) {
            console.error("Failed to load saved crosshair offset:", e);
        }
    }
  }

  // Toggle debug visualization and logging
  toggleDebug() {
    this.debug = !this.debug;
    
    if (this.debug) {
      console.log("🔫 Weapon debug mode ENABLED");
      console.log("- Projectile paths will be visualized");
      console.log("- Detailed logs will be shown in console");
      
      // You can also enable debugging in ProjectilePool
      if (window.projectilePool) {
        window.projectilePool.debug = true;
        console.log("- ProjectilePool debugging enabled");
      }
      
      return "Debug mode enabled";
    } else {
      console.log("🔫 Weapon debug mode DISABLED");
      
      // Also disable ProjectilePool debugging
      if (window.projectilePool) {
        window.projectilePool.debug = false;
      }
      
      return "Debug mode disabled";
    }
  }
}